import { useEffect, useId, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CircleAlert, KeyRound, PlugZap, Server } from 'lucide-react'
import { Role } from '@shared/types'
import type { SiteDTO, SshKeyDTO, TestConnectionResponse } from '@shared/types'
import { errorMessage, isApiError } from '@/lib/api'
import { Badge, Button, Input, Modal, Spinner } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { useSitesStore } from '@/stores/useSitesStore'

/** The same rule the server enforces — `installName` reaches a shell command. */
const INSTALL_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,60}$/

const INSTALL_NAME_RULE =
  'Use lowercase letters, numbers and hyphens only, starting with a letter or number, up to 61 characters.'

export interface ConnectSiteDialogProps {
  open: boolean
  onClose: () => void
  /**
   * Used only to tell whether a usable key exists — there is no key picker any more.
   * A single WP Engine key is designated for the whole instance, so this list holds
   * that one key (the server includes it regardless of who uploaded it) and the
   * server resolves it without being told.
   */
  sshKeys: SshKeyDTO[]
  keysLoading?: boolean
  /** Fired after the site is created, before this dialog navigates away. */
  onCreated?: (site: SiteDTO) => void
}

function validateInstallName(name: string): string | null {
  if (!name) return 'Enter your WP Engine install name.'
  if (name.includes('.')) {
    return 'That looks like a domain. Use the install name from your WP Engine portal — for example tinytowncampgr, not tinytowncampground.com.'
  }
  if (/[A-Z]/.test(name)) return `Install names are lowercase. ${INSTALL_NAME_RULE}`
  if (!INSTALL_NAME_PATTERN.test(name)) return INSTALL_NAME_RULE
  return null
}

export default function ConnectSiteDialog({
  open,
  onClose,
  sshKeys,
  keysLoading = false,
  onCreated
}: ConnectSiteDialogProps): React.ReactElement {
  const navigate = useNavigate()
  const test = useSitesStore((state) => state.test)
  const create = useSitesStore((state) => state.create)
  const isAdmin = useAuthStore((state) => state.user?.role) === Role.Admin

  const formId = useId()
  const [installName, setInstallName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [result, setResult] = useState<TestConnectionResponse | null>(null)
  const [testing, setTesting] = useState(false)
  const [connecting, setConnecting] = useState(false)

  useEffect(() => {
    if (!open) return
    setInstallName('')
    setNameError(null)
    setSubmitError(null)
    setResult(null)
  }, [open])

  const hasKeys = sshKeys.length > 0
  const busy = testing || connecting

  const handleTest = async (): Promise<void> => {
    const name = installName.trim()
    const problem = validateInstallName(name)
    setNameError(problem)
    setSubmitError(null)
    if (problem) return

    setTesting(true)
    setResult(null)
    try {
      setResult(await test(name))
    } catch (cause) {
      setResult({ ok: false, error: errorMessage(cause) })
    } finally {
      setTesting(false)
    }
  }

  const handleConnect = async (): Promise<void> => {
    const name = installName.trim()
    const problem = validateInstallName(name)
    setNameError(problem)
    setSubmitError(null)
    if (problem) return

    setConnecting(true)
    try {
      const site = await create(name)
      onCreated?.(site)
      onClose()
      navigate(`/sites/${site.id}`)
    } catch (cause) {
      // Drop the earlier "Connected" banner: it described a probe that has since
      // been superseded, and showing it beside a fresh failure reads as a
      // contradiction.
      setResult(null)
      const fieldMessage = isApiError(cause) ? cause.details?.installName?.[0] : undefined
      if (fieldMessage) setNameError(fieldMessage)
      else if (isApiError(cause) && cause.status === 409) setNameError(cause.message)
      else setSubmitError(errorMessage(cause))
    } finally {
      setConnecting(false)
    }
  }

  const footer = hasKeys ? (
    <>
      <Button variant="secondary" disabled={busy} onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="secondary"
        iconLeft={<PlugZap />}
        loading={testing}
        disabled={connecting || installName.trim().length === 0}
        onClick={() => void handleTest()}
      >
        Test connection
      </Button>
      <Button
        type="submit"
        form={formId}
        loading={connecting}
        disabled={testing || installName.trim().length === 0}
      >
        Connect
      </Button>
    </>
  ) : keysLoading ? (
    <Button variant="secondary" onClick={onClose}>
      Cancel
    </Button>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>
        {isAdmin ? 'Cancel' : 'Close'}
      </Button>
      {/* Only an admin can fix this, and only an admin can reach /keys. Sending a
          regular user there would land them on a 404 or an empty page. */}
      {isAdmin ? (
        <Button
          iconLeft={<KeyRound />}
          onClick={() => {
            onClose()
            navigate('/keys')
          }}
        >
          Set up the WP Engine key
        </Button>
      ) : null}
    </>
  )

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose()
      }}
      title="Connect a WP Engine site"
      description="Argus reads the media library over SSH. Nothing is written to WordPress until you approve and save alt text."
      footer={footer}
    >
      {keysLoading && !hasKeys ? (
        <div className="flex items-center gap-3 py-4 text-sm text-fg-muted">
          <Spinner size="sm" />
          Checking the WP Engine connection…
        </div>
      ) : !hasKeys ? (
        <div className="flex gap-3 rounded-control border border-warn-500/40 bg-warn-100/60 p-3.5 dark:bg-warn-950/50">
          <KeyRound
            className="mt-0.5 size-4 shrink-0 text-warn-700 dark:text-warn-300"
            aria-hidden="true"
          />
          <div className="min-w-0 space-y-1.5 text-sm leading-relaxed">
            <p className="font-semibold text-warn-700 dark:text-warn-300">
              WP Engine access isn&rsquo;t set up yet
            </p>
            {/* Two audiences, two messages: the admin gets the action, everyone else
                gets told who to ask instead of a dead end they cannot resolve. */}
            {isAdmin ? (
              <p className="text-fg-muted">
                Argus reads media libraries over SSH, and no key has been configured for this
                instance yet.{' '}
                <Link
                  to="/keys"
                  onClick={onClose}
                  className="font-medium text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
                >
                  Add the WP Engine key
                </Link>{' '}
                — the page includes a short setup guide. You only do this once, for everyone.
              </p>
            ) : (
              <p className="text-fg-muted">
                Argus reads media libraries over SSH, and an administrator needs to set the WP Engine
                key up first. This is a one-time setup for the whole team — ask an administrator to
                add it, then you can connect sites without any key of your own.
              </p>
            )}
          </div>
        </div>
      ) : (
        <form
          id={formId}
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault()
            void handleConnect()
          }}
        >
          <Input
            label="Install name"
            value={installName}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            placeholder="tinytowncampgr"
            error={nameError ?? undefined}
            hint="The WP Engine install name, not the domain — e.g. tinytowncampgr, not tinytowncampground.com."
            onChange={(event) => {
              setInstallName(event.target.value)
              setNameError(null)
              setResult(null)
            }}
          />

          <div aria-live="polite" className="space-y-3">
            {result?.ok === true ? (
              <div className="flex flex-wrap items-center gap-2 rounded-control border border-ok-500/40 bg-ok-100/60 px-3 py-2.5 text-sm dark:bg-ok-950/50">
                <Badge variant="success" size="sm">
                  Connected
                </Badge>
                <span className="text-fg">WordPress {result.wpVersion ?? 'unknown'}</span>
                {result.siteUrl ? (
                  <span className="min-w-0 truncate text-fg-muted">{result.siteUrl}</span>
                ) : null}
              </div>
            ) : null}

            {result && result.ok === false ? (
              <div className="flex gap-2.5 rounded-control border border-bad-500/40 bg-bad-100/60 px-3 py-2.5 dark:bg-bad-950/50">
                <CircleAlert
                  className="mt-0.5 size-4 shrink-0 text-bad-700 dark:text-bad-300"
                  aria-hidden="true"
                />
                <div className="min-w-0 space-y-1 text-sm leading-relaxed">
                  <p className="font-semibold text-bad-700 dark:text-bad-300">
                    Connection failed
                  </p>
                  <p className="break-words text-fg-muted">
                    {result.error ?? 'WP Engine refused the connection.'}
                  </p>
                </div>
              </div>
            ) : null}

            {submitError ? (
              <div
                role="alert"
                className="rounded-control border border-bad-500/40 bg-bad-100/60 px-3 py-2.5 text-sm text-bad-700 dark:bg-bad-950/50 dark:text-bad-300"
              >
                {submitError}
              </div>
            ) : null}
          </div>

          <div className="flex gap-2.5 border-t border-line pt-4 text-xs leading-relaxed text-fg-muted">
            <Server className="mt-0.5 size-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
            <p>
              Argus connects as installname@installname.ssh.wpengine.net using the WP Engine key set
              up for this instance. Connecting also tests the SSH connection and records the
              WordPress version. Fetch the media library from the site&rsquo;s page once it is
              connected.
            </p>
          </div>
        </form>
      )}
    </Modal>
  )
}

export { ConnectSiteDialog }
